import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Wrench, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';

const DEMO_EMAIL = 'demo@company.com';
const DEMO_PASSWORD = 'Demo2024!';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const [email, setEmail] = useState(isDemo ? DEMO_EMAIL : '');
  const [password, setPassword] = useState(isDemo ? DEMO_PASSWORD : '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isDemo);
  const autoLoginRanRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Auto-login when ?demo=true is present
  useEffect(() => {
    if (!isDemo || autoLoginRanRef.current) return;
    autoLoginRanRef.current = true;
    (async () => {
      const result = await login(DEMO_EMAIL, DEMO_PASSWORD);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Demo account is currently unavailable. Please try again.');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Back to Home */}
          <Link
            to="/"
            data-testid="back-to-home-link"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo and Title */}
          <div className="text-left">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-500 rounded-sm flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <span className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                WorkStream Pro
              </span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              {isDemo ? 'Demo login' : 'Welcome Back'}
            </h1>
            <p className="mt-2 text-slate-400">
              {isDemo ? 'Opening the shared sample workspace' : 'Sign in to manage your work orders'}
            </p>
          </div>

          {/* Demo Account Info Banner */}
          <div className="bg-cyan-900/30 border border-cyan-500/50 rounded-sm p-4" data-testid="demo-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                {isDemo && loading ? (
                  <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                ) : (
                  <Wrench className="w-5 h-5 text-cyan-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-cyan-400 font-semibold mb-1">
                  {isDemo && loading ? 'Signing you into the demo workspace…' : 'Try Our Demo Account'}
                </h3>
                <p className="text-slate-300 text-sm mb-3">
                  {isDemo && loading
                    ? 'Loading pre-seeded data so you can explore in seconds.'
                    : 'Test all features with pre-loaded sample data'}
                </p>
                <div className="bg-slate-950/50 rounded-sm p-3 space-y-2 font-mono text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Email:</span>
                    <span className="text-cyan-300 font-medium">demo@company.com</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Password:</span>
                    <span className="text-cyan-300 font-medium">Demo2024!</span>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="demo-autofill-btn"
                  onClick={() => {
                    setEmail(DEMO_EMAIL);
                    setPassword(DEMO_PASSWORD);
                  }}
                  className="mt-3 text-xs text-cyan-400 hover:text-cyan-300 underline"
                >
                  Click to auto-fill credentials
                </button>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/50 rounded-sm text-red-400" data-testid="login-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-400 uppercase text-xs tracking-wider">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="input-industrial w-full"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-400 uppercase text-xs tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="input-industrial w-full"
                data-testid="login-password-input"
              />
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-blue-400 hover:text-blue-300 transition"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Sign up link */}
          <p className="text-center text-slate-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-amber-500 hover:text-amber-400 font-medium" data-testid="signup-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-transparent z-10" />
        <img
          src="https://images.unsplash.com/photo-1584980770387-586712373e40?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Maintenance"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 z-20 max-w-md">
          <p className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight text-white">
            Track Every Task
          </p>
          <p className="mt-2 text-slate-400">
            From report to completion, never lose track of your maintenance work again.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
